import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";

const About = () => {
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          "@id": "https://namebiolab.lovable.app/about#webpage",
          "url": "https://namebiolab.lovable.app/about",
          "name": "About NameBioLab - AI-Powered Username & Bio Generator",
          "description": "Learn about NameBioLab's mission to help content creators, influencers, and businesses create compelling online identities with AI-powered username and bio generation.",
          "isPartOf": {
            "@id": "https://namebiolab.lovable.app/#webapp"
          },
          "breadcrumb": {
            "@id": "https://namebiolab.lovable.app/about#breadcrumb"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://namebiolab.lovable.app/about#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://namebiolab.lovable.app/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "About",
              "item": "https://namebiolab.lovable.app/about"
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <article className="prose prose-slate dark:prose-invert max-w-none">
          <h1>About NameBioLab</h1>
          
          <section>
            <h2>Our Mission</h2>
            <p>
              NameBioLab is dedicated to helping content creators, influencers, businesses, and social media enthusiasts 
              create compelling online identities. We believe that your username and bio are the first impression you make 
              online, and we're here to make that impression count.
            </p>
          </section>

          <section>
            <h2>What We Do</h2>
            <p>
              Our AI-powered platform generates creative, unique usernames and engaging bios tailored to your personality, 
              brand, or business. Whether you're starting a new Instagram account, refreshing your TikTok profile, or 
              establishing your presence on YouTube, Twitter, or any social media platform, NameBioLab provides instant, 
              personalized suggestions.
            </p>
          </section>

          <section>
            <h2>Why Choose NameBioLab?</h2>
            <ul>
              <li>
                <strong>100% Free:</strong> No hidden fees, no premium tiers. Our tool is completely free to use, 
                forever. We believe everyone deserves access to quality branding tools.
              </li>
              <li>
                <strong>No Login Required:</strong> Start generating immediately without creating an account. 
                We respect your privacy and don't require personal information.
              </li>
              <li>
                <strong>AI-Powered Creativity:</strong> Our advanced AI understands context, trends, and what makes 
                a username or bio memorable and effective.
              </li>
              <li>
                <strong>Multiple Themes:</strong> Choose from funny, cool, aesthetic, or professional styles to match 
                your brand personality perfectly.
              </li>
              <li>
                <strong>Instant Results:</strong> Get 5 usernames and 5 bios in seconds. No waiting, no hassle.
              </li>
              <li>
                <strong>Mobile Friendly:</strong> Use NameBioLab on any device - desktop, tablet, or smartphone. 
                Our responsive design works everywhere.
              </li>
            </ul>
          </section>

          <section>
            <h2>How It Works</h2>
            <p>
              NameBioLab uses cutting-edge artificial intelligence to analyze your input and generate personalized 
              suggestions. Simply enter a few keywords about yourself, your brand, or your interests, choose what you 
              want to generate (usernames, bios, or both), select a theme that matches your style, and let our AI do 
              the rest.
            </p>
            <p>
              Our AI considers factors like memorability, uniqueness, platform compatibility, and current social media 
              trends to ensure your username stands out and your bio captures attention.
            </p>
          </section>

          <section>
            <h2>Who Uses NameBioLab?</h2>
            <ul>
              <li><strong>Content Creators:</strong> YouTubers, streamers, and podcasters looking for memorable usernames</li>
              <li><strong>Influencers:</strong> Instagram and TikTok personalities wanting engaging bios</li>
              <li><strong>Small Businesses:</strong> Entrepreneurs establishing their social media presence</li>
              <li><strong>Gamers:</strong> Players seeking unique gaming handles</li>
              <li><strong>Students:</strong> Young adults creating their first social media accounts</li>
              <li><strong>Brands:</strong> Marketing teams brainstorming brand names and taglines</li>
            </ul>
          </section>

          <section>
            <h2>Our Commitment</h2>
            <p>
              We're committed to providing a high-quality, user-friendly tool that respects your privacy and delivers 
              real value. We don't collect personal data, we don't spam you with emails, and we don't gate our features 
              behind paywalls. Our goal is simple: help you create an amazing online presence.
            </p>
          </section>

          <section>
            <h2>Contact Us</h2>
            <p>
              Have questions, suggestions, or feedback? We'd love to hear from you. Visit our{" "}
              <Link to="/contact" className="text-primary hover:underline">contact page</Link> to get in touch with our team.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};

export default About;
